import {
  EditableListDataGridLayout,
  EditableListFormShowTemplate,
  EDITABLE_LIST_CUSTOM,
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
  EDITABLE_LIST_NESTED_LIST,
} from 'framework/list';
import { FormattedTextField } from 'framework/ui/field';
import {
  FTEBadge,
  ProjectTeamMemberPositionField,
  ProjectTeamMemberRoleField,
  ProjectTeamSlotPositionField,
  TeamMemberField,
} from 'domain/shared/newProject';
import { Grid2 as Grid, Typography } from '@mui/material';
import { useShowContext } from 'framework/show';
import AssignmentActions from './AssignmentActions';
import { Tooltip } from 'framework/ui/mui-builder';
import { StaffingAssignmentListMember, StaffingProjectAssignment } from 'domain/shared/staffing';
import { SLOT_LIST_SOURCE } from '../constants';
import { AssignmentDateRangeField } from 'domain/shared/assignment';

const AssignmentActionsCell = ({
  onReload,
  assignmentsById,
  projectStartDate,
}: {
  onReload: () => Promise<void>;
  assignmentsById: Record<string, StaffingProjectAssignment>;
  projectStartDate?: string;
}) => {
  const { data } = useShowContext<StaffingAssignmentListMember>();
  const memberData = data.getData() as StaffingAssignmentListMember;
  const sourceAssignment = assignmentsById[String(memberData.assignmentId)];

  return (
    <AssignmentActions
      assignment={{
        ...memberData,
        isVirtual: sourceAssignment?.isVirtual || false,
      }}
      onReload={onReload}
      projectStartDate={projectStartDate}
    />
  );
};

const ConfirmAssignmentsSlotList = ({
  onReload,
  assignmentsById,
  projectStartDate,
}: {
  onReload: () => Promise<void>;
  assignmentsById: Record<string, StaffingProjectAssignment>;
  projectStartDate?: string;
}) => {
  return (
    <EditableListFormShowTemplate listSource={SLOT_LIST_SOURCE}>
      <EditableListDataGridLayout
        disabledAddNewRow
        disabledRemove
        customEnptyMessage="No assignments available for this project."
        rowProps={{
          sx: {
            pb: 0,
          },
        }}
        getRowEnabled={() => true}
        getTableCells={() =>
          Promise.resolve([
            {
              type: EDITABLE_LIST_GROUP,
              component: Grid,
              componentProps: {
                container: true,
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '16px',
                columnSpacing: 1,
                columns: 12,
              },
              gridCellProps: {
                size: 12,
              },
              children: [
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'positionId',
                  isEditor: false,
                  component: ProjectTeamSlotPositionField,
                  gridCellProps: { size: 2 },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'requiredFte',
                  isEditor: false,
                  component: FTEBadge,
                  componentProps: {
                    assignedFteSource: 'assignedFte',
                  },
                  gridCellProps: {
                    size: 10,
                  },
                },
              ],
            },
            {
              type: EDITABLE_LIST_GROUP,
              component: Grid,
              componentProps: {
                container: true,
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 1,
                borderBottom: '1px solid var(--mui-palette-TableCell-border)',
              },
              gridCellProps: {
                size: 12,
              },
              children: [
                {
                  type: EDITABLE_LIST_NESTED_LIST,
                  source: 'members',
                  component: Grid,
                  gridCellProps: { size: 12 },
                  rows: {
                    emptyListComponent: (
                      <Typography variant="body2" color="textSecondary">
                        No assignments in this slot.
                      </Typography>
                    ),
                  },
                  componentProps: {
                    direction: 'row',
                    container: true,
                    columnSpacing: 1,
                    py: 2,
                    columns: 12,
                  },
                  children: [
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'employeeAlias',
                      isEditor: false,
                      component: TeamMemberField,
                      gridCellProps: {
                        size: 2,
                      },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'fte',
                      isEditor: false,
                      component: FTEBadge,
                      componentProps: {
                        isMemberFter: true,
                      },
                      gridCellProps: {
                        size: 1,
                      },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'positionId',
                      isEditor: false,
                      component: ProjectTeamMemberPositionField,
                      componentProps: {
                        positionIdSource: 'positionId',
                        technologyIdSource: 'technologyId',
                        competencyLevelSource: 'competencyLevel',
                      },
                      gridCellProps: {
                        size: 2,
                      },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'role',
                      isEditor: false,
                      component: ProjectTeamMemberRoleField,
                      gridCellProps: {
                        size: 1.5,
                      },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'startDate',
                      isEditor: false,
                      component: AssignmentDateRangeField,
                      gridCellProps: { size: 2 },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'noteText',
                      isEditor: false,
                      component: FormattedTextField,
                      componentProps: {
                        format: (str: string) =>
                          str && str.length > 40 ? (
                            <Tooltip title={str}>
                              <div>
                                <Typography
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 1.2,
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {str}
                                </Typography>
                              </div>
                            </Tooltip>
                          ) : (
                            str
                          ),
                      },
                      gridCellProps: {
                        size: 2,
                      },
                    },
                    {
                      type: EDITABLE_LIST_CUSTOM,
                      component: AssignmentActionsCell,
                      componentProps: { onReload, assignmentsById, projectStartDate },
                      gridCellProps: {
                        size: 1.5,
                        sx: { display: 'flex', justifyContent: 'flex-end' },
                      },
                    },
                  ],
                },
              ],
            },
          ])
        }
      />
    </EditableListFormShowTemplate>
  );
};

export default ConfirmAssignmentsSlotList;
